import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Star, Trophy } from "lucide-react";
import api from "@/lib/api";

export default function ReviewPhotoStrip() {
  const [photos, setPhotos] = useState([]);
  const [winner, setWinner] = useState(null);
  useEffect(() => {
    api.get("/reviews/photos", { params: { limit: 24 } }).then(({ data }) => setPhotos(data.photos)).catch(() => {});
    api.get("/pup-of-the-wall").then(({ data }) => setWinner(data.winner)).catch(() => {});
  }, []);
  if (photos.length === 0 && !winner) return null;
  const base = photos.length === 0 ? [] : photos.length < 6 ? Array.from({ length: Math.ceil(6 / photos.length) }, () => photos).flat() : photos;
  const loop = [...base, ...base]; // two identical halves so the -50% marquee loops seamlessly
  return (
    <section className="py-10 sm:py-14 overflow-hidden" id="pup-of-the-wall" data-testid="review-photo-strip">
      {winner && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-8">
          <Link to="/pup-of-the-wall" className="relative flex flex-col sm:flex-row items-stretch gap-0 rounded-[2rem] overflow-hidden bg-[#19324A] text-white soft-shadow group" data-testid="pup-of-the-wall">
            <img src={winner.photo} alt={`${winner.author_name}'s pup`} className="w-full sm:w-72 h-56 sm:h-auto object-cover group-hover:scale-[1.02] transition-transform duration-500" />
            <div className="p-6 sm:p-8 flex-1 flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 self-start rounded-full bg-[#FFD93D] text-[#19324A] text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1"><Trophy className="w-3.5 h-3.5" /> Pup of the Wall · {winner.month}</span>
              <h3 className="font-display font-extrabold text-2xl sm:text-3xl mt-3 !text-white" data-testid="pup-of-the-wall-title">{winner.author_name}'s pup takes the crown 🏆</h3>
              <p className="text-white/80 text-sm mt-2">{winner.note || `Spotted on the review wall with ${winner.product?.name || "our gear"} — Chewie & Solo couldn't resist those eyes.`}</p>
              <p className="text-xs text-white/60 mt-4">Want your bulldog up here? Add a pup pic to your review — every photo earns 10% off, and the monthly winner gets a prize code.</p>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-[#FFD93D] mt-3 group-hover:underline" data-testid="pup-of-the-wall-hall-link">See past winners →</span>
            </div>
          </Link>
        </div>
      )}
      {photos.length > 0 && (<>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-3 mb-5">
        <span className="w-10 h-10 rounded-2xl bg-[#FF6B9D]/15 flex items-center justify-center"><Camera className="w-5 h-5 text-[#FF6B9D]" /></span>
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#FF6B9D]">Pup-approved</p>
          <h2 className="font-display font-extrabold text-xl sm:text-2xl text-[#19324A]">Real pups, real reviews</h2>
        </div>
      </div>
      <div className="relative group" style={{ maskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)" }}>
        <div className="flex gap-3 sm:gap-4 w-max hb-photo-marquee" data-testid="review-photo-track">
          {loop.map((p, i) => (
            <Link key={`${p.review_id}-${i}`} to={`/product/${p.product.slug}#reviews`} className="relative block w-40 h-40 sm:w-52 sm:h-52 rounded-3xl overflow-hidden soft-shadow shrink-0 rotate-[-1.5deg] even:rotate-[1.5deg] hover:rotate-0 transition-transform duration-300" data-testid={i < base.length ? `review-photo-${i}` : undefined} aria-label={`${p.author_name}'s pup with ${p.product.name}`}>
              <img src={p.photo} alt={`${p.author_name}'s pup with ${p.product.name}`} loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#19324A]/80 to-transparent p-3 text-white">
                <p className="text-xs font-bold truncate">{p.author_name}</p>
                <p className="text-[10px] opacity-85 truncate flex items-center gap-1"><Star className="w-3 h-3 fill-[#FFD93D] text-[#FFD93D]" />{p.rating} · {p.product.name}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
      </>)}
    </section>
  );
}
